import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../api/client';

/**
 * «Отправить в Telegram» (ТЗ §2.20).
 *
 * Канал выбирается из справочника, а не вводится руками: разговор клиента —
 * персональные данные, и «куда угодно по номеру чата» означает отправку, у
 * которой потом не найти концов. Каждая отправка ложится в аудит на сервере.
 */
interface Channel {
  id: number;
  name: string;
}

async function fetchChannels(): Promise<Channel[]> {
  const { data } = await apiClient.get<{ channels: Channel[] }>('/telephony/share-channels');

  return data.channels;
}

export function ShareCallButton({ callId }: { callId: number }) {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const { data: channels = [] } = useQuery({
    queryKey: ['telephony', 'share-channels'],
    queryFn: fetchChannels,
    enabled: open,
    retry: false,
  });

  const send = async (channelId: number) => {
    setSending(true);
    setResult(null);

    try {
      const { data } = await apiClient.post<{ channel: string }>(`/telephony/calls/${callId}/share`, {
        channel_id: channelId,
      });
      setResult(`Отправлено в «${data.channel}»`);
    } catch (e: unknown) {
      const message = (e as { response?: { data?: { message?: string } } }).response?.data?.message;
      setResult(message ?? 'Отправка не удалась.');
    } finally {
      setSending(false);
      setOpen(false);
    }
  };

  return (
    <span className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="rounded-ops-sm px-2 py-1 text-ops-label"
        style={{ background: 'var(--ops-surface-2)', color: 'var(--ops-text)' }}
      >
        📤 в Telegram
      </button>

      {open ? (
        <div
          className="absolute right-0 z-40 mt-1 w-56 rounded-ops-md p-1"
          style={{ background: 'var(--ops-surface)', boxShadow: 'var(--ops-shadow-pop)' }}
        >
          {channels.length === 0 ? (
            <div className="px-2 py-2 text-ops-label" style={{ color: 'var(--ops-text-secondary)' }}>
              Каналы не заведены — их добавляет владелец в настройках телефонии.
            </div>
          ) : null}

          {channels.map((channel) => (
            <button
              key={channel.id}
              type="button"
              disabled={sending}
              onClick={() => void send(channel.id)}
              className="block w-full rounded-ops-sm px-2 py-1.5 text-left text-ops-label disabled:opacity-50"
              style={{ color: 'var(--ops-text)' }}
            >
              {channel.name}
            </button>
          ))}
        </div>
      ) : null}

      {result ? (
        <span className="ml-2 text-ops-micro" style={{ color: 'var(--ops-text-secondary)' }}>
          {result}
        </span>
      ) : null}
    </span>
  );
}
